import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import dayjs from "dayjs";

import { MeetingListingMobile } from "../../../components/Meetings/MeetingListingMobile";
import { MeetingListingDesktop } from "../../../components/Meetings/MeetingListingDesktop";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { Medias } from "../../../enumns/medias";
import { useAuth } from "../../../contexts/AuthContext";
import { useIsRegistrationIncomplete } from "./useIsRegistrationIncomplete";
import { useMeetingsByAttendeeEmail } from "../../../services/hooks/meetings/queries/useMeetingsByAttendeeEmail";
import { AlertModal } from "../../../components/AlertModal";
import { useExpertMutation } from "../../../services/hooks/experts/mutations/useExpertMutation";
import Markdown from "react-markdown";
import { expertGuide } from "../../../mocks/expertGuide";
import { TextWithImage } from "./components/TextWithImage";
import { Topics } from "./components/Topics";
import { TextWithImageInverse } from "./components/TextWithImageInverse";
import { Text } from "./components/Text";
import { Tab } from "./Tab";
import { MeetingModels } from "./components/MeetingModels";
import { Cards } from "./components/Cards";
import { Faq } from "./components/FAQ";

export const Meetings = () => {
  const navigate = useNavigate();
  const isTabletSize = useMediaQuery(`(min-width: ${Medias.MD}px)`);

  const { user } = useAuth();
  const { data, isSuccess } = useMeetingsByAttendeeEmail(user?.email!);

  const { mutate } = useExpertMutation(user!.id);

  const { formatMessage, locale } = useIntl();

  const shouldShowNotification = user!.showNotification;
  const canShowNotification = dayjs(new Date(user!.createdAt)).isBefore(
    new Date(2024, 2, 7)
  ); // 07/03/2024
  const [openDialogModal, setOpenDialogModal] = useState<boolean>(
    shouldShowNotification && canShowNotification
  );

  const isRegistrationIncomplete = useIsRegistrationIncomplete(user?.id!);

  const activateDialogModal = (isItToRedirect: boolean) => {
    mutate({
      id: user!.id,
      data: {
        showNotification: false,
        agreeParticipateMeet: user?.agreeParticipateMeet,
        doNotContactTemporarily: user?.doNotContactTemporarily,
        nationality: user?.nationality,
        bankCountry: user?.bankCountry,
        meetingLanguages: user?.meetingLanguages,
        otherLanguage: user?.otherLanguage,
      },
    });

    if (isItToRedirect) {
      navigate("/expert/term-of-participation");
    } else {
      setOpenDialogModal(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <AlertModal isOpen={openDialogModal}>
        <div className="pb-10 px-5">
          <div className="pb-5 w-full max-w-sm text-center mx-auto">
            <p className="text-black font-bold">
              {formatMessage({ id: "TERMS_OF_PARTICIPATION_WERE_UPDATED" })}
            </p>
            <p className="text-black">
              {formatMessage({ id: "TERMS_OF_PARTICIPATION_UPDATE_MESSAGE" })}
            </p>
          </div>
          <div className="flex flex-row justify-between max-w-md mx-auto">
            <button
              type="button"
              className="p-4 py-3 text-base font-bold text-center text-primary-light rounded-lg bg-white"
              onClick={() => activateDialogModal(false)}
            >
              {formatMessage({ id: "FINISH_LATER" })}
            </button>
            <button
              type="button"
              className="w-40 p-4 py-3 text-base font-bold text-center whitespace-nowrap text-white rounded-lg bg-primary-light"
              onClick={() => activateDialogModal(true)}
            >
              {formatMessage({ id: "READ_TERMS" })}
            </button>
          </div>
        </div>
      </AlertModal>
      {data?.length > 0 && isSuccess && (
        <div className="flex-1 pb-4">
          <>
            {isTabletSize ? (
              <MeetingListingDesktop
                meetings={data}
                messageSpace={isRegistrationIncomplete}
              />
            ) : (
              <MeetingListingMobile
                meetings={data}
                messageSpace={isRegistrationIncomplete}
              />
            )}
          </>
        </div>
      )}

      {data?.length === 0 && (
        <>
          <div className="flex justify-end items-center mb-6 pr-2">
            {isRegistrationIncomplete && (
              <div className="flex flex-col gap-6 p-4 text-center rounded-lg md:gap-28 bg-primary-light md:flex-row md:max-w-fit md:items-center md:justify-center">
                <p className="text-base font-semibold text-white">
                  {formatMessage({ id: "NOTICE_FILL_REGISTRATION" })}
                </p>
                <button
                  className="p-2 px-6 mx-auto text-sm md:text-lg font-medium text-white bg-transparent border-[1px] border-white rounded-lg hover:opacity-80 md:mx-0"
                  onClick={() => navigate("/expert/complete-registration")}
                >
                  {formatMessage({ id: "COMPLETE_REGISTRATION" })}
                </button>
              </div>
            )}
          </div>
          <div className="leading-7 pb-10">
            <TextWithImage />
            <div className="pt-16">
              <Topics
                title={formatMessage({ id: "GUIDE_FOR_EXPERT_TOPICS_1_TITLE" })}
                subtitle={formatMessage({
                  id: "GUIDE_FOR_EXPERT_TOPICS_1_SUBTITLE",
                })}
                content={formatMessage({
                  id: "GUIDE_FOR_EXPERT_TOPICS_1_CONTENT",
                })}
              />
              <Topics
                title={formatMessage({ id: "GUIDE_FOR_EXPERT_TOPICS_2_TITLE" })}
                subtitle={formatMessage({
                  id: "GUIDE_FOR_EXPERT_TOPICS_2_SUBTITLE",
                })}
                content={formatMessage({
                  id: "GUIDE_FOR_EXPERT_TOPICS_2_CONTENT",
                })}
              />
            </div>
            <TextWithImageInverse />
            <Text />
            <div className="pt-16">
              <Tab
                title={formatMessage({ id: "GUIDE_FOR_EXPERT_TAB_TITLE" })}
                content={formatMessage({ id: "GUIDE_FOR_EXPERT_TAB_CONTENT" })}
              />
              <Tab
                title={formatMessage({ id: "GUIDE_FOR_EXPERT_TAB_TITLE_2" })}
                content={formatMessage({
                  id: "GUIDE_FOR_EXPERT_TAB_CONTENT_2",
                })}
              />
            </div>
            <MeetingModels />
            <Cards />
            <Faq />
          </div>
        </>
      )}
    </div>
  );
};
